import { realizeEvent, useEventsForSiteLevelType } from "../reducers/eventSlice";
import useClientConfig from "../common/hooks/clientConfigHook";
import { useTranslation } from "react-i18next";
import Select from "../common/components/Select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cdate, now } from "../common/utils/CDate";
import Button from "../common/components/Button";
import Icon from "../common/components/Icon";
import { EventInfoDialog } from "./EventInfo";
import { propagateFields } from "./customCtrlUtils";

export const customCtrlDef = {
    toString: async ({ value, defaultValue }) => {
        if (!value) {
            return defaultValue;
        }
        const event = await realizeEvent(value);
        return event?.name ?? defaultValue;
    },
    control: ({ value, def, onChange, site, level, type, event, context: { args } = {}, ...props}) =>
        <EventDropdown site={site ?? args?.site ?? def.site} level={level ?? args?.level ?? def.level} type={type ?? args?.type ?? def.typeParam} event={event} onChange={value => propagateFields(value, def.field, onChange)} wildcard={def.wildcard} {...props}></EventDropdown>
}

const EventDropdown = ({ id, site, level, type, event, wildcard, placeholder, disabled, required, onChange, isSearch, readonly = false}) => {
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    const events = useEventsForSiteLevelType(site, level, type);
    const [showEventInfo, setShowEventInfo] = useState(false);

    const handleChange = useCallback(id => {
        let event = events.find(event => event.id == id);
        if (!event) {
            onChange({ event: null });
        }
        let update = { event: event.id };
        if (clientConfig.enableSites && event.site && event.site !== site) {
            update.site = event.site
        }
        if (clientConfig.enableLevels && event.level && event.level !== level) {
            update.level = event.level;
        }
        if (event.type && event.type !== type) {
            update.type = event.type
        }
        onChange(update);
    }, [clientConfig, events, site, level, type, onChange]);

    const displayEvents = useMemo(() => {
        if (isSearch) {
            return events ?? [];
        }
        return events?.filter(e => !e.active || e.active.some(a => now(clientConfig.tz).isBetween(cdate(a.start, clientConfig.tz), cdate(a.end, clientConfig.tz))) || e.id == event) ?? [];
    }, [event, events, isSearch, clientConfig.tz]);

    useEffect(() => {
        if (!isSearch && displayEvents?.filter(event => event.active?.length > 0)?.length === 1 && !event) {
            onChange({ event: displayEvents.filter(event => event.active?.length > 0)[0].id })
        }
    }, [displayEvents, isSearch, event, onChange])

    return <>
    <EventInfoDialog eventid={event} showModal={showEventInfo} onClose={() => setShowEventInfo(false)}></EventInfoDialog>
    <Select readonly={readonly} id={id} placeholder={placeholder ?? t('event', { count: 1 })} wildcard={wildcard} value={event} onChange={handleChange} 
        appendCtrl={<Button variant="outline-dark" disabled={!event} onClick={() => setShowEventInfo(true)}><Icon variant="solid" kind="info-circle"></Icon></Button>}
        disabled={disabled} required={required} options={displayEvents} labelField="name" valueField="id"></Select>
    </>
}

export default EventDropdown;